import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { organizationAtom } from "../../state/atoms";
import { Organization } from "../../types/organizations";
import { isProduction } from "../../services/config";
import { isEmbeddedPath } from "../../services/embedded";
import { detectAuthCrm, isAuthPath } from "./authCrm";

/** The CRM the dashboard dresses up as. */
export type Crm = "outreach" | "hubspot";

const PREVIEW_PARAM = "crmTheme";
const PREVIEW_STORAGE_KEY = "boring-crm-theme-preview";

/**
 * Development and staging only: `?crmTheme=hubspot` (or `outreach`) shows the
 * dashboard in that CRM's look whatever the org's CRM is, so the HubSpot look
 * can be checked and screenshotted without a HubSpot org. It sticks for the
 * tab's session; `?crmTheme=off` drops it. Production ignores the parameter.
 */
export const readCrmPreview = (search: string): Crm | null => {
  if (isProduction) return null;
  try {
    const param = new URLSearchParams(search).get(PREVIEW_PARAM);
    if (param === "hubspot" || param === "outreach") {
      sessionStorage.setItem(PREVIEW_STORAGE_KEY, param);
      return param;
    }
    if (param !== null) {
      sessionStorage.removeItem(PREVIEW_STORAGE_KEY);
      return null;
    }
    const saved = sessionStorage.getItem(PREVIEW_STORAGE_KEY);
    return saved === "hubspot" || saved === "outreach" ? saved : null;
  } catch {
    return null;
  }
};

const LAST_CRM_KEY = "boring-last-crm";

/**
 * The CRM this browser last showed. Until the organization loads there is no
 * CRM to go by, and guessing Outreach dressed a HubSpot user's loading screen
 * (frame and skeletons) in Outreach's look.
 */
const rememberedCrm = (): Crm | null => {
  try {
    const v = window.localStorage.getItem(LAST_CRM_KEY);
    return v === "hubspot" || v === "outreach" ? v : null;
  } catch {
    return null;
  }
};

const rememberCrm = (crm: Crm) => {
  try {
    if (window.localStorage.getItem(LAST_CRM_KEY) !== crm) window.localStorage.setItem(LAST_CRM_KEY, crm);
  } catch {
    // Private windows: the next load guesses again.
  }
};

/** The CRM the org was set up on; for an older org without one, HubSpot only when it is the sole connection. Before it loads, the one this browser last showed. */
export const crmOfOrganization = (organization: Organization | null | undefined): Crm => {
  if (!organization) return rememberedCrm() ?? "outreach";
  let crm: Crm;
  if (organization.initialIntegrationType) crm = organization.initialIntegrationType;
  else {
    const hubspot = organization.isHubSpotAuthorized === true || !!organization.hubSpotAuthentication;
    const outreach = organization.isOutreachAuthorized === true || !!organization.outreachAuthentication;
    crm = hubspot && !outreach ? "hubspot" : "outreach";
  }
  rememberCrm(crm);
  return crm;
};

/**
 * The CRM look for the current page. The pages that run inside Outreach or
 * HubSpot frames (/outreach/..., /hubspot/...) keep their own styling, so
 * they always get the default.
 */
export const useCrm = (): Crm => {
  const { pathname, search } = useLocation();
  const organization = useRecoilValue(organizationAtom);
  if (isEmbeddedPath(pathname)) return "outreach";
  // Signed out there is no organization: the sign-in pages follow ?crm= and
  // what this browser chose before (authCrm.ts).
  if (isAuthPath(pathname)) return readCrmPreview(search) ?? detectAuthCrm(search);
  return readCrmPreview(search) ?? crmOfOrganization(organization);
};

export const useIsHubSpotCrm = (): boolean => useCrm() === "hubspot";

/** The CRM's name for copy: "HubSpot" or "Outreach". */
export const useCrmName = (): string => (useCrm() === "hubspot" ? "HubSpot" : "Outreach");
